import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Building2, Package, TrendingUp, Loader, X, Check,
  AlertTriangle, Euro, Truck, Users, ChevronRight, ChevronLeft, Info, RefreshCw
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import SearchInput from '../components/SearchInput';

const API_URL = process.env.REACT_APP_API_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Utilidades
// ─────────────────────────────────────────────────────────────────────────────
const fmtMoney = (n) =>
  n == null || isNaN(n) ? '—' : Number(n).toLocaleString('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 4
  });

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

// ─────────────────────────────────────────────────────────────────────────────
// Modal de subida por porcentaje
// ─────────────────────────────────────────────────────────────────────────────
// Hook defensivo: garantiza que todos los colores existen aunque el ThemeContext aun no haya resuelto
const useSafeTheme = () => {
  const { theme } = useTheme();
  return {
    primary:       theme?.primary       || '#3cbf9a',
    primaryDark:   theme?.primaryDark   || '#2da080',
    primaryLight:  theme?.primaryLight  || 'rgba(60, 191, 154, 0.10)',
    primaryBorder: theme?.primaryBorder || 'rgba(60, 191, 154, 0.20)',
    secondary:     theme?.secondary     || '#1a73e8',
  };
};

const SubidaModal = ({ open, onClose, grupo, proveedor, onSuccess }) => {
  const theme = useSafeTheme();
  const [pct, setPct] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    if (open) {
      setPct('');
      setPreview(null);
      setError('');
      setConfirmStep(false);
    }
  }, [open]);

  if (!open) return null;

  const handlePreview = async () => {
    setError('');
    const num = parseFloat(pct);
    if (isNaN(num) || num === 0) {
      setError('Introduce un porcentaje distinto de 0');
      return;
    }
    setLoadingPreview(true);
    try {
      const r = await fetch(`${API_URL}/api/precios-grupo/preview`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          matrizId: grupo.id,
          proveedorId: proveedor.id,
          porcentaje: num
        })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || data.error || 'Error en preview');
      setPreview(data);
      setConfirmStep(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApply = async () => {
    setError('');
    setApplying(true);
    try {
      const r = await fetch(`${API_URL}/api/precios-grupo/aplicar-porcentaje`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          matrizId: grupo.id,
          proveedorId: proveedor.id,
          porcentaje: parseFloat(pct)
        })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || data.error || 'Error al aplicar');
      onSuccess?.(data);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setApplying(false);
    }
  };

  const num = parseFloat(pct);
  const factor = isNaN(num) ? null : 1 + num / 100;
  const isSubida = num > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ background: `linear-gradient(to right, ${theme.primaryLight}, ${theme.primaryLight})` }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: theme.primaryLight, color: theme.primary }}
            >
              <TrendingUp size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Subida masiva de precios</h2>
              <p className="text-xs text-gray-600">
                Grupo: <span className="font-semibold">{grupo.razon_social}</span>
                {' · '}
                Proveedor: <span className="font-semibold">{proveedor.nombre}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg transition-colors"
            disabled={applying}
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">

          {!confirmStep && (
            <>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Porcentaje a aplicar
              </label>
              <div className="flex items-center gap-2 mb-1">
                <div className="relative flex-1">
                  <input
                    type="number"
                    step="0.01"
                    value={pct}
                    onChange={(e) => setPct(e.target.value)}
                    placeholder="Ej: 5  (subida)  /  -3.5  (bajada)"
                    className="w-full pl-3 pr-10 py-3 text-lg border-2 border-gray-300 focus:outline-none rounded-lg transition-colors"
                    style={{ '--tw-ring-color': theme.primary }}
                    onFocus={(e) => e.target.style.borderColor = theme.primary}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                    disabled={loadingPreview}
                  />
                  <span className="absolute inset-y-0 right-3 flex items-center text-gray-400 font-semibold">%</span>
                </div>
              </div>
              {!isNaN(num) && num !== 0 && (
                <p
                  className="text-sm mt-1 font-medium"
                  style={{ color: isSubida ? theme.primary : '#ea580c' }}
                >
                  Factor: × {factor.toFixed(4)} {isSubida ? '(subida)' : '(bajada)'}
                </p>
              )}

              <div
                className="mt-6 p-4 rounded-lg flex gap-3 border"
                style={{ backgroundColor: theme.primaryLight, borderColor: theme.primaryBorder }}
              >
                <Info size={18} className="flex-shrink-0 mt-0.5" style={{ color: theme.primary }} />
                <div className="text-sm leading-relaxed" style={{ color: theme.primaryDark }}>
                  Esta accion se aplicara a <strong>todos los clientes del grupo</strong> y a
                  <strong> todos los productos del proveedor</strong>. Los productos que algun cliente
                  del grupo no tenga aun, se daran de alta automaticamente al precio de referencia
                  del grupo y luego se les aplicara el mismo porcentaje. Esto es <strong>irreversible</strong>.
                </div>
              </div>
            </>
          )}

          {confirmStep && preview && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center border">
                  <Package size={20} className="text-gray-400 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-gray-800">{preview.productos}</div>
                  <div className="text-xs text-gray-500 uppercase">Productos</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center border">
                  <Users size={20} className="text-gray-400 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-gray-800">{preview.clientes}</div>
                  <div className="text-xs text-gray-500 uppercase">Clientes</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-200">
                  <RefreshCw size={20} className="text-amber-500 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-amber-700">{preview.filas_actualizar}</div>
                  <div className="text-xs text-amber-600 uppercase">A actualizar</div>
                </div>
                <div
                  className="rounded-lg p-4 text-center border"
                  style={{ backgroundColor: theme.primaryLight, borderColor: theme.primaryBorder }}
                >
                  <Check size={20} className="mx-auto mb-1" style={{ color: theme.primary }} />
                  <div className="text-2xl font-bold" style={{ color: theme.primaryDark }}>{preview.filas_insertar}</div>
                  <div className="text-xs uppercase" style={{ color: theme.primary }}>Nuevas (alta)</div>
                </div>
              </div>

              <div className="mb-2 text-sm font-bold text-gray-700 flex items-center gap-2">
                <Euro size={14} /> Muestra de cambios (max. 5)
              </div>
              {preview.muestra.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Sin filas existentes para previsualizar.</p>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700">Producto</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700">Cliente</th>
                        <th className="text-right px-3 py-2 font-semibold text-gray-700">Anterior</th>
                        <th className="text-right px-3 py-2 font-semibold text-gray-700">Nuevo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.muestra.map((m, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2 text-gray-800 truncate max-w-[180px]" title={m.producto_nombre}>
                            {m.producto_nombre}
                          </td>
                          <td className="px-3 py-2 text-gray-600 truncate max-w-[140px]" title={m.cliente_nombre}>
                            {m.cliente_nombre}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-500">{fmtMoney(m.precio_anterior)}</td>
                          <td
                            className="px-3 py-2 text-right font-bold"
                            style={{ color: isSubida ? theme.primary : '#ea580c' }}
                          >
                            {fmtMoney(m.precio_nuevo)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-5 p-4 bg-amber-50 border border-amber-300 rounded-lg flex gap-3">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 leading-relaxed">
                  Vas a aplicar <strong>{num > 0 ? '+' : ''}{num}%</strong> a {preview.productos} productos
                  en {preview.clientes} clientes. Se actualizaran <strong>{preview.filas_actualizar}</strong> filas
                  existentes y se daran de alta <strong>{preview.filas_insertar}</strong> nuevas.
                  Confirma para ejecutar.
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end gap-3">
          {!confirmStep ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                disabled={loadingPreview}
              >
                Cancelar
              </button>
              <button
                onClick={handlePreview}
                disabled={loadingPreview || !pct}
                className="px-5 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                style={{ backgroundColor: theme.primary }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = theme.primaryDark)}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.primary}
              >
                {loadingPreview ? <Loader size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                Calcular preview
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setConfirmStep(false)}
                disabled={applying}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Volver
              </button>
              <button
                onClick={handleApply}
                disabled={applying}
                className="px-5 py-2 text-sm font-semibold text-white disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2"
                style={{ backgroundColor: theme.primary }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = theme.primaryDark)}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.primary}
              >
                {applying ? <Loader size={16} className="animate-spin" /> : <Check size={16} />}
                Confirmar y aplicar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Pagina principal
// ─────────────────────────────────────────────────────────────────────────────
export default function PreciosGrupo() {
  const { user } = useContext(AuthContext);
  const theme = useSafeTheme();
  const isAdmin = user?.permisos === 1;

  const [grupos, setGrupos] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [grupoBusqueda, setGrupoBusqueda] = useState('');
  const [grupoSel, setGrupoSel] = useState(null);

  const [proveedores, setProveedores] = useState([]);
  const [proveedorSel, setProveedorSel] = useState(null);
  const [loadingProv, setLoadingProv] = useState(false);

  const [productos, setProductos] = useState([]);
  const [loadingProd, setLoadingProd] = useState(false);
  const [productoBusqueda, setProductoBusqueda] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const [clientesGrupo, setClientesGrupo] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // ── Carga inicial de grupos ─────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    setLoadingGrupos(true);
    fetch(`${API_URL}/api/precios-grupo/grupos`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setGrupos(d.grupos || []))
      .catch(e => console.error('Error cargando grupos:', e))
      .finally(() => setLoadingGrupos(false));
  }, [isAdmin]);

  // ── Cuando se elige un grupo, cargar proveedores y clientes ─────────────
  useEffect(() => {
    if (!grupoSel) {
      setProveedores([]);
      setClientesGrupo([]);
      return;
    }
    setProveedorSel(null);
    setProductos([]);
    setLoadingProv(true);
    Promise.all([
      fetch(`${API_URL}/api/precios-grupo/${grupoSel.id}/proveedores`, { headers: authHeaders() })
        .then(r => r.json())
        .then(d => setProveedores(d.proveedores || [])),
      fetch(`${API_URL}/api/precios-grupo/${grupoSel.id}/clientes`, { headers: authHeaders() })
        .then(r => r.json())
        .then(d => setClientesGrupo(d.clientes || []))
    ])
      .catch(e => console.error('Error cargando datos del grupo:', e))
      .finally(() => setLoadingProv(false));
  }, [grupoSel]);

  // ── Cuando se elige un proveedor, cargar productos ──────────────────────
  useEffect(() => {
    if (!grupoSel || !proveedorSel) {
      setProductos([]);
      return;
    }
    setLoadingProd(true);
    const params = new URLSearchParams({ proveedor_id: proveedorSel.id });
    if (productoBusqueda.trim()) params.append('search', productoBusqueda.trim());
    fetch(`${API_URL}/api/precios-grupo/${grupoSel.id}/productos?${params}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setProductos(d.productos || []))
      .catch(e => console.error('Error cargando productos:', e))
      .finally(() => setLoadingProd(false));
  }, [grupoSel, proveedorSel, productoBusqueda]);

  // Reset paginacion cuando cambia el conjunto de productos
  useEffect(() => { setPage(1); }, [grupoSel, proveedorSel, productoBusqueda]);

  const totalPages = Math.max(1, Math.ceil(productos.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const productosPage = productos.slice(pageStart, pageStart + PAGE_SIZE);

  const gruposFiltrados = useMemo(() => {
    const t = grupoBusqueda.trim().toUpperCase();
    if (!t) return grupos;
    return grupos.filter(g =>
      (g.razon_social || '').toUpperCase().includes(t) ||
      (g.alias || '').toUpperCase().includes(t) ||
      (g.cif || '').toUpperCase().includes(t)
    );
  }, [grupos, grupoBusqueda]);

  const handleSubidaSuccess = (data) => {
    setToast({
      type: 'success',
      msg: `${data.filas_actualizadas} precios actualizados, ${data.filas_insertadas} nuevos productos dados de alta (${data.porcentaje > 0 ? '+' : ''}${data.porcentaje}%)`
    });
    setTimeout(() => setToast(null), 7000);
    // Recargar productos para reflejar cambios
    if (grupoSel && proveedorSel) {
      const params = new URLSearchParams({ proveedor_id: proveedorSel.id });
      if (productoBusqueda.trim()) params.append('search', productoBusqueda.trim());
      fetch(`${API_URL}/api/precios-grupo/${grupoSel.id}/productos?${params}`, { headers: authHeaders() })
        .then(r => r.json())
        .then(d => setProductos(d.productos || []));
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl shadow p-10 text-center max-w-md">
          <AlertTriangle size={40} className="mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Acceso restringido</h2>
          <p className="text-gray-600 text-sm">
            Esta seccion es solo para administradores.<br />
            Tu nivel de permisos: {user?.permisos ?? 'no definido'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50 flex">

      {/* ── Sidebar: lista de grupos ──────────────────────────────────── */}
      <aside className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <Building2 size={18} style={{ color: theme.primary }} />
            Grupos de clinicas
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            Cada grupo es una matriz con sus clinicas hijas
          </p>
        </div>

        <div className="p-3 border-b">
          <SearchInput
            value={grupoBusqueda}
            onChange={(e) => setGrupoBusqueda(e.target.value)}
            placeholder="Buscar grupo..."
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {loadingGrupos ? (
            <div className="p-8 text-center text-gray-400">
              <Loader size={20} className="mx-auto animate-spin" />
              <p className="text-xs mt-2">Cargando grupos...</p>
            </div>
          ) : gruposFiltrados.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              {grupoBusqueda ? 'Sin coincidencias' : 'Sin grupos disponibles'}
            </div>
          ) : (
            gruposFiltrados.map(g => {
              const selected = grupoSel?.id === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => setGrupoSel(g)}
                  className="w-full text-left p-3 border-b transition-colors"
                  style={selected
                    ? { backgroundColor: theme.primaryLight, borderLeft: `4px solid ${theme.primary}` }
                    : {}}
                  onMouseEnter={(e) => !selected && (e.currentTarget.style.backgroundColor = theme.primaryLight)}
                  onMouseLeave={(e) => !selected && (e.currentTarget.style.backgroundColor = '')}
                >
                  <div className="font-semibold text-gray-800 text-sm truncate">{g.razon_social}</div>
                  <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                    <span>{g.localidad || '—'}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Users size={11} /> {g.num_hijas} hija{g.num_hijas !== 1 ? 's' : ''}
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* ── Panel principal ──────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto">
        {!grupoSel ? (
          <div className="h-full flex items-center justify-center text-center p-10">
            <div>
              <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700">Selecciona un grupo</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-sm">
                Elige un grupo de clinicas a la izquierda para ver sus proveedores y precios.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6 w-full">

            {/* Cabecera del grupo */}
            <div className="bg-white rounded-xl shadow-sm border p-5 mb-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Grupo seleccionado</div>
                  <h1 className="text-2xl font-bold text-gray-800">{grupoSel.razon_social}</h1>
                  <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-3">
                    <span>CIF: {grupoSel.cif || '—'}</span>
                    <span>·</span>
                    <span>{grupoSel.localidad || '—'}, {grupoSel.provincia || '—'}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2"
                    style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}
                  >
                    <Users size={14} /> {clientesGrupo.length} clinica{clientesGrupo.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Lista de clinicas del grupo */}
              {clientesGrupo.length > 0 && (
                <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                  {clientesGrupo.map(c => {
                    const esMatriz = c.id === grupoSel.id;
                    return (
                      <span
                        key={c.id}
                        className="text-xs px-2.5 py-1 rounded-full border"
                        style={esMatriz
                          ? { backgroundColor: theme.primaryLight, color: theme.primaryDark, borderColor: theme.primaryBorder, fontWeight: 600 }
                          : { backgroundColor: '#f3f4f6', color: '#374151', borderColor: '#e5e7eb' }}
                        title={c.razon_social}
                      >
                        {esMatriz ? '★ ' : ''}{c.alias || c.razon_social}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Selector de proveedor + boton subida */}
            <div className="bg-white rounded-xl shadow-sm border p-5 mb-5">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-2 flex items-center gap-1">
                    <Truck size={12} /> Proveedor
                  </label>
                  <select
                    value={proveedorSel?.id || ''}
                    onChange={(e) => {
                      const p = proveedores.find(x => x.id === parseInt(e.target.value));
                      setProveedorSel(p || null);
                    }}
                    disabled={loadingProv || proveedores.length === 0}
                    className="w-full px-3 py-2.5 border-2 border-gray-300 focus:outline-none rounded-lg text-sm bg-white disabled:bg-gray-100 transition-colors"
                    onFocus={(e) => e.target.style.borderColor = theme.primary}
                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  >
                    <option value="">
                      {loadingProv
                        ? 'Cargando proveedores...'
                        : proveedores.length === 0
                          ? 'Este grupo no tiene proveedores asignados'
                          : '— Selecciona un proveedor —'}
                    </option>
                    {proveedores.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => setModalOpen(true)}
                  disabled={!proveedorSel}
                  className="px-5 py-2.5 text-white text-sm font-semibold rounded-lg shadow hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  style={{ background: `linear-gradient(to right, ${theme.primary}, ${theme.primaryDark})` }}
                  title={!proveedorSel ? 'Selecciona un proveedor primero' : 'Aplicar % a todos los productos'}
                >
                  <TrendingUp size={16} />
                  Subir / bajar precios %
                </button>
              </div>
            </div>

            {/* Listado de productos */}
            {proveedorSel && (
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    <Package size={16} style={{ color: theme.primary }} />
                    Productos de {proveedorSel.nombre}
                    <span className="text-sm font-normal text-gray-500">({productos.length})</span>
                  </h3>
                  <SearchInput
                    className="w-72"
                    value={productoBusqueda}
                    onChange={(e) => setProductoBusqueda(e.target.value)}
                    placeholder="Buscar producto..."
                  />
                </div>

                {loadingProd ? (
                  <div className="p-12 text-center text-gray-400">
                    <Loader size={24} className="mx-auto animate-spin" />
                    <p className="text-sm mt-2">Cargando productos...</p>
                  </div>
                ) : productos.length === 0 ? (
                  <div className="p-12 text-center text-gray-400 text-sm">
                    <Package size={40} className="mx-auto text-gray-300 mb-2" />
                    {productoBusqueda
                      ? 'Sin coincidencias para esa busqueda'
                      : 'Este proveedor no tiene productos asignados'}
                  </div>
                ) : (
                  <>
                    <table className="w-full text-sm table-fixed">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 w-[40%]">Producto</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 w-[15%]">Marca</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 w-[15%]">Ref.</th>
                          <th className="text-left px-4 py-3 font-semibold text-gray-700 w-[15%]">Familia</th>
                          <th className="text-right px-4 py-3 font-semibold text-gray-700 w-[15%]">Precio referencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productosPage.map(p => (
                          <tr key={p.id} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2.5 text-gray-800 font-medium truncate" title={p.nombre}>{p.nombre}</td>
                            <td className="px-4 py-2.5 text-gray-600 truncate" title={p.marca || ''}>{p.marca || '—'}</td>
                            <td className="px-4 py-2.5 text-gray-600 font-mono text-xs truncate" title={p.ref_proveedor || ''}>{p.ref_proveedor || '—'}</td>
                            <td className="px-4 py-2.5 text-gray-600 text-xs truncate" title={p.familia || ''}>{p.familia || '—'}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-gray-800">
                              {p.precio_referencia != null ? (
                                <span className="inline-flex items-center gap-1.5">
                                  {fmtMoney(p.precio_referencia)}
                                  {!p.tiene_matriz && (
                                    <span
                                      className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                                      style={{ backgroundColor: '#fef3c7', color: '#b45309' }}
                                      title="La matriz no tiene este producto. Se usa el precio de otra clinica del grupo."
                                    >
                                      hijo
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-amber-600 text-xs italic">sin alta en grupo</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Paginacion */}
                    <div className="border-t px-4 py-3 flex items-center justify-between bg-gray-50">
                      <div className="text-xs text-gray-600">
                        Mostrando <strong>{pageStart + 1}</strong>–<strong>{Math.min(pageStart + PAGE_SIZE, productos.length)}</strong> de <strong>{productos.length}</strong>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="px-2 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1 text-xs"
                        >
                          <ChevronLeft size={14} /> Anterior
                        </button>
                        <div className="flex items-center gap-1 mx-2">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                            .reduce((acc, n, idx, arr) => {
                              if (idx > 0 && n - arr[idx - 1] > 1) acc.push('...');
                              acc.push(n);
                              return acc;
                            }, [])
                            .map((n, i) =>
                              n === '...' ? (
                                <span key={`gap-${i}`} className="text-gray-400 px-1 text-xs">…</span>
                              ) : (
                                <button
                                  key={n}
                                  onClick={() => setPage(n)}
                                  className="w-8 h-8 rounded text-xs font-semibold transition-colors"
                                  style={n === page
                                    ? { backgroundColor: theme.primary, color: '#fff' }
                                    : { backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db' }}
                                >
                                  {n}
                                </button>
                              )
                            )}
                        </div>
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="px-2 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1 text-xs"
                        >
                          Siguiente <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <SubidaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        grupo={grupoSel}
        proveedor={proveedorSel}
        onSuccess={handleSubidaSuccess}
      />

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 max-w-sm px-4 py-3 rounded-lg shadow-xl text-white"
          style={{ backgroundColor: toast.type === 'success' ? theme.primary : '#dc2626' }}
        >
          <div className="flex items-start gap-2">
            {toast.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
            <span className="text-sm font-medium">{toast.msg}</span>
          </div>
        </div>
      )}
    </div>
  );
}
